/**
 * SECCIONES SEO DE COLECCIÓN
 * ---------------------------------------------------------------------------------------------------------------------
 * Los dos componentes de las secciones que alimentan los metacampos de SEO:
 *
 *   <seo-read-more>  el «Ver más» del bloque de texto (sections/seo-collection-content.liquid)
 *   <seo-faq>        el acordeón de preguntas frecuentes (sections/seo-collection-faq.liquid)
 *
 * Van juntos en un asset —y no en un <script> dentro de cada sección— porque las dos conviven en la misma página de
 * colección: así el navegador descarga y cachea este archivo una vez en lugar de repetir el código en el HTML de cada
 * colección. Cada sección lo pide con <script src defer>; el guardia de customElements evita que la segunda petición
 * vuelva a registrar nada. El CSS que los acompaña está en theme.css.
 *
 * El código es exactamente el que estaba incrustado: se movió sin tocar una línea.
 */

if (!customElements.get('seo-read-more')) {
  customElements.define(
    'seo-read-more',
    class SeoReadMore extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[data-seo-read-more-body]');
        this.toggle = this.querySelector('[data-seo-read-more-toggle]');

        if (!this.body || !this.toggle) return;

        this.labelMore = this.toggle.querySelector('[data-seo-label-more]');
        this.labelLess = this.toggle.querySelector('[data-seo-label-less]');

        this.setup();
        this.toggle.addEventListener('click', this.onToggle.bind(this));

        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => {
            if (this.toggle.getAttribute('aria-expanded') === 'false') this.setup();
          });
          this.observer.observe(this.body);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      get collapsedHeight() {
        const value = getComputedStyle(this).getPropertyValue('--seo-collapsed-height');
        return parseFloat(value) || 0;
      }

      setup() {
        const overflows = this.body.scrollHeight > this.collapsedHeight + 24;

        if (overflows) {
          this.body.classList.add('is-collapsed');
          this.body.style.maxHeight = '';
          this.toggle.hidden = false;
        } else {
          this.body.classList.remove('is-collapsed');
          this.body.style.maxHeight = '';
          this.toggle.hidden = true;
        }
      }

      onToggle() {
        const expanded = this.toggle.getAttribute('aria-expanded') === 'true';

        this.body.style.maxHeight = this.body.scrollHeight + 'px';
        void this.body.offsetHeight;

        if (expanded) {
          this.body.classList.add('is-collapsed');
          this.body.style.maxHeight = '';
        } else {
          this.body.classList.remove('is-collapsed');

          const clear = () => {
            this.body.style.maxHeight = '';
            clearTimeout(fallback);
          };

          this.body.addEventListener('transitionend', clear, { once: true });
          const fallback = setTimeout(clear, 500);
        }

        this.toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (this.labelMore) this.labelMore.hidden = !expanded;
        if (this.labelLess) this.labelLess.hidden = expanded;
      }
    }
  );
}

if (!customElements.get('seo-faq')) {
  customElements.define(
    'seo-faq',
    class SeoFaq extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('details').forEach((item) => {
          const summary = item.querySelector('summary');
          if (summary) summary.addEventListener('click', (event) => this.onSummaryClick(event, item));
        });
      }

      onSummaryClick(event, item) {
        if (!item.open || item.classList.contains('is-closing')) return;

        event.preventDefault();

        const answer = item.querySelector('[data-seo-faq-answer]');
        if (!answer) {
          item.open = false;
          return;
        }

        const close = () => {
          item.classList.remove('is-closing');
          item.open = false;
          clearTimeout(timer);
        };

        item.classList.add('is-closing');
        answer.addEventListener('transitionend', close, { once: true });
        const timer = setTimeout(close, 400);
      }
    }
  );
}
